import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { invoiceKeys, jobKeys } from '@/lib/queryKeys';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api/client';
import { normalizeInvoice } from './useInvoice';
import { reportDelivery } from './reportDelivery';
import { computeEffectiveInvoiceStatus } from '@/lib/invoice-status';
import type { Invoice, InvoiceDeliveryOutcome, SendInvoiceDto } from '@/types/invoice';

type SentInvoice = Invoice & { delivery?: InvoiceDeliveryOutcome };

type SendBody = Omit<SendInvoiceDto, 'id' | 'issued'>;

async function postSend(endpoint: string, body: SendBody): Promise<SentInvoice> {
  const raw = await apiPost<Record<string, unknown>, SendBody>(operationsClient, endpoint, body);
  // `normalizeInvoice` only maps known invoice fields, so pull `delivery`
  // off the raw response before it is dropped.
  return {
    ...normalizeInvoice(raw),
    delivery: raw.delivery as InvoiceDeliveryOutcome | undefined,
  };
}

/** One leg of a `both` send. Never throws — a transport failure is an outcome. */
async function attemptLeg(
  endpoint: string,
  body: SendBody,
): Promise<{ invoice?: SentInvoice; delivery?: InvoiceDeliveryOutcome; error?: unknown }> {
  try {
    const invoice = await postSend(endpoint, body);
    return { invoice, delivery: invoice.delivery };
  } catch (error) {
    return {
      error,
      delivery: {
        attempted: true,
        channel: body.communicationOption === 'sms' ? 'sms' : 'email',
        delivered: false,
        suppressed: false,
        error: error instanceof Error ? error.message : 'Delivery failed',
      },
    };
  }
}

/**
 * Best-effort routing hint. A cache MISS is genuinely unknown, not a draft —
 * but guessing costs nothing now that `/send` refusing an already-issued
 * invoice is recovered from below, and guessing "draft" keeps the common case
 * (issue a new invoice) to a single request.
 */
function cachedInvoiceIsIssued(queryClient: QueryClient, id: string): boolean {
  const cached = queryClient.getQueryData<Invoice>(invoiceKeys.detail(id));
  return cached ? computeEffectiveInvoiceStatus(cached) !== 'draft' : false;
}

/**
 * The server's refusal of `/send` for an invoice that is no longer a DRAFT.
 *
 * It answers 403 — the same status a permission denial uses — so the message
 * is the only thing separating "this one is already issued" from "you may not
 * send this". Match both, so a real authorization failure still reaches the
 * operator as one instead of being retried against an endpoint that would
 * refuse it for the same reason.
 */
function isAlreadyIssuedRefusal(error: unknown): boolean {
  const candidate = error as { status?: number; message?: string } | null;
  return (
    candidate?.status === 403 && /only draft invoices can be sent/i.test(candidate.message ?? '')
  );
}

/**
 * One outcome for two legs, merged the way the server merges its own `both`
 * (deliverInvoiceCommunication): delivered only when no leg FAILED — a
 * suppressed (opted-out) leg is a deliberate no-send, not a failure — and the
 * first failing leg's error is kept so a partial send never reads as success.
 */
function mergeDelivery(
  email: InvoiceDeliveryOutcome | undefined,
  sms: InvoiceDeliveryOutcome | undefined,
): InvoiceDeliveryOutcome | undefined {
  const legs = [email, sms].filter((leg): leg is InvoiceDeliveryOutcome => Boolean(leg));
  if (legs.length === 0) return undefined;
  const failed = legs.filter((leg) => leg.attempted && !leg.delivered && !leg.suppressed);
  return {
    attempted: legs.some((leg) => leg.attempted),
    channel: 'both',
    delivered: failed.length === 0 && legs.some((leg) => leg.delivered),
    suppressed: legs.every((leg) => leg.suppressed),
    error: failed[0]?.error ?? null,
  };
}

export function useSendInvoice() {
  const queryClient = useQueryClient();

  return useApiMutation<SentInvoice, SendInvoiceDto>({
    mutationFn: async ({ id, issued, ...body }) => {
      // `/send` issues a DRAFT and delivers; it refuses an invoice that is
      // already OPEN. An issued invoice — typically one auto-issued on job
      // completion while the tenant's auto-send is off, so the customer never
      // received anything — goes out through the channel endpoints, which
      // accept OPEN and PARTIALLY_PAID and return the same invoice + delivery
      // shape. A caller that does not say falls back to the cached invoice,
      // so the edit form's plain `{ id }` still takes the right door.
      const isIssued = issued ?? cachedInvoiceIsIssued(queryClient, id);
      if (!isIssued) {
        try {
          return await postSend(invoiceEndpoints.send(id), body);
        } catch (error) {
          if (!isAlreadyIssuedRefusal(error)) throw error;
          // The invoice is OPEN on the server even though we routed it as a
          // draft. The payment-schedule engine creates an invoice and issues
          // it as a SEPARATE step, so a page loaded in that gap — or one whose
          // detail cache was never populated — shows a DRAFT the server has
          // since opened. Deliver it the way an issued invoice is delivered
          // rather than surfacing a 403 that reads as "you lack permission".
        }
      }
      const option = body.communicationOption ?? 'email';
      if (option !== 'both') {
        return postSend(
          option === 'sms' ? invoiceEndpoints.sendSms(id) : invoiceEndpoints.sendEmail(id),
          body,
        );
      }
      // The server fans `both` out only on the issue path; on an issued
      // invoice it is two legs, each attempted regardless of the other, and
      // reported as one outcome the way the server merges its own. A leg
      // whose transport throws (5xx, timeout) is a failed delivery, not a
      // failed mutation — the other leg may already have reached the
      // customer, and rejecting here would hide that and invite a resend.
      const [email, sms] = await Promise.all([
        attemptLeg(invoiceEndpoints.sendEmail(id), { ...body, communicationOption: 'email' }),
        attemptLeg(invoiceEndpoints.sendSms(id), { ...body, communicationOption: 'sms' }),
      ]);
      const invoice = sms.invoice ?? email.invoice;
      if (!invoice) throw (email.error ?? sms.error) as Error;
      return { ...invoice, delivery: mergeDelivery(email.delivery, sms.delivery) };
    },
    onSuccess: (data) => {
      queryClient.setQueryData<Invoice>(invoiceKeys.detail(data.id), (current) =>
        current
          ? {
              ...current,
              ...data,
              contact: data.contact ?? current.contact,
            }
          : data,
      );
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(data.id) });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      if (data.jobId) {
        void queryClient.invalidateQueries({ queryKey: jobKeys.detail(data.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(data.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(data.jobId) });
      }
      reportDelivery(data.delivery);
    },
  });
}
